import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { MatchListDocument, SortOrder } from "@/graphql/generated/operations";

// Die vollen Daten der verknüpften Matches.
//
// `Project.matches` in genesis liefert absichtlich nur Id und Name: ein Match
// zusammenzusetzen ist Sache des MatchControllers samt seinen Loadern. Für die
// Kachel braucht es aber Modus, Beschreibung, Kandidatenzahl und Urheber.
//
// Geholt wird das über die Liste der eigenen Matches, die anschliessend auf die
// verknüpften Ids gefiltert wird. Nicht schön, aber der einzige Weg ohne
// zusätzliche Abfrage pro Match: `MatchFilterInput` kennt nur `name` und
// `description`, nach Id kann man Matches serverseitig nicht suchen. Die Folge
// ist eine Grenze, keine Fehlerquelle: wer mehr als PAGE Matches hat und ein
// älteres verknüpft, sieht dessen Kachel hier nicht.

const PAGE = 100;

export function useLinkedMatches(ids: readonly string[]) {
  const result = useQuery(MatchListDocument, {
    variables: { input: { first: PAGE, order: { updatedAt: SortOrder.Desc } } },
    skip: ids.length === 0,
  });
  const nodes = result.data?.matches?.nodes;
  return useMemo(() => {
    const byId = new Map((nodes ?? []).map((m) => [m.id, m]));
    // Reihenfolge der Verknüpfung, nicht die der Abfrage: im Projekt steht das
    // zuerst verknüpfte Match zuerst.
    return ids.flatMap((id) => {
      const match = byId.get(id);
      return match ? [match] : [];
    });
  }, [ids, nodes]);
}

export type LinkedMatch = ReturnType<typeof useLinkedMatches>[number];
